/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAccountState } from "../../redux/reducers/accountReducer";
import { CaseData, CaseStatus, CaseSystemStatus, UserAccount } from "../types";
import { useApi } from "../api/useApi";
import { useCaseDataState } from "../../redux/reducers/caseDataReducer";
import { useCaseDataActions } from "../../redux/actions/caseDataActions";
import { useCaseDataSignalR } from "../../signalR/useCaseDataSignalR";

export const CaseDataApiRoutes = {
    getCaseData: (accountId: string) => `account/${accountId}`,
    upsertCaseData: (accountId: string) => `account/${accountId}`,
    addRemoveCaseType: (accountId: string, caseType: string) => `account/${accountId}/caseType/${caseType}`,
    addCaseStatus: (accountId: string) => `account/${accountId}/caseStatus`,
    upsertCaseStatuses: (accountId: string) => `account/${accountId}/caseStatuses`,
    removeCaseStatus: (accountId: string, systemStatus: string, status: string) =>
        `account/${accountId}/caseStatus/${systemStatus}/${status}`,
    addRemoveExpenseType: (accountId: string, expenseType: string) => `account/${accountId}/expenseType/${expenseType}`,
};
export type CaseDataService = {
    loadCaseDataAsync: () => Promise<void>;
    caseData?: CaseData;
    caseTypes: string[];
    caseStatuses: CaseStatus[];
    getCaseStatus: (cs: CaseStatus) => CaseStatus | undefined;
    userCreatedStatuses: CaseStatus[];
    expenseTypes: string[];
    addCaseType: (caseType: string) => Promise<void>;
    removeCaseType: (caseType: string) => Promise<void>;
    addCaseStatus: (caseStatus: CaseStatus) => Promise<void>;
    upsertCaseStatuses: (caseStatuses: CaseStatus[]) => Promise<void>;
    removeCaseStatus: (caseStatus: CaseStatus) => Promise<void>;
    moveStatusToIndex: (caseStatus: CaseStatus, newIndex: number) => void;
    addExpenseType: (expenseType: string) => Promise<void>;
    removeExpenseType: (expenseType: string) => Promise<void>;
    getTheseStatuses: (statuses?: CaseStatus[]) => CaseStatus[] | undefined;
}
/**
 * This service will load caseData for the account into Redux state and provide methods to interact with it.
 * @returns CaseDataService
 */
export const useCaseDataService = (): CaseDataService => {

    //Hooks
    const { primaryAccount } = useAccountState();
    const { account } = primaryAccount as UserAccount ?? {};
    const caseDataState = useCaseDataState();
    const caseDataStateActions = useCaseDataActions();
    
    //Api
    const api = useApi<CaseData>(`api/CaseData`);

    //Data Calls
    const loadCaseDataAsync = async () => {
        const caseData = await api.getAsync(CaseDataApiRoutes.getCaseData(account?.accountId as string));
        caseDataStateActions.setCaseData(caseData);
    }
    const upsertCaseData = async (caseData: CaseData) => {
        await api.postAsync(caseData, CaseDataApiRoutes.upsertCaseData(account?.accountId as string));
    }
    const addCaseType = async (caseType: string) => {
        const safeCaseType = encodeURIComponent(caseType);
        await api.postAsync({}, CaseDataApiRoutes.addRemoveCaseType(account?.accountId as string, safeCaseType));

        caseDataStateActions.addType(caseType);
    }
    const removeCaseType = async (caseType: string) => {
        await api.deleteAsync([], CaseDataApiRoutes.addRemoveCaseType(account?.accountId as string, caseType));
        caseDataStateActions.deleteType(caseType);
    }
    const addCaseStatus = async (caseStatus: CaseStatus) => {
        await api.postAsync(caseStatus, CaseDataApiRoutes.addCaseStatus(account?.accountId as string));
        caseDataStateActions.addStatus(caseStatus);
    }
    const upsertCaseStatuses = async (caseStatuses: CaseStatus[]) => {
        caseStatuses.forEach((cs) => {
            caseDataStateActions.addStatus(cs);
        });
        await api.postAsync(caseStatuses, CaseDataApiRoutes.upsertCaseStatuses(account?.accountId as string));
    };
    const removeCaseStatus = async (caseStatus: CaseStatus) => {
        const safeSystemStatus = encodeURIComponent(caseStatus.systemStatus);
        const safeStatus = encodeURIComponent(caseStatus.status);
        await api.deleteAsync([], CaseDataApiRoutes.removeCaseStatus(account?.accountId as string, safeSystemStatus, safeStatus));
        caseDataStateActions.deleteStatus(caseStatus);
    }
    const addExpenseType = async (expenseType: string) => {
        await api.postAsync({}, CaseDataApiRoutes.addRemoveExpenseType(account?.accountId as string, expenseType));
        caseDataStateActions.addExpenseType(expenseType);
    }
    const removeExpenseType = async (expenseType: string) => {
        await api.deleteAsync([], CaseDataApiRoutes.addRemoveExpenseType(account?.accountId as string, expenseType));
        caseDataStateActions.deleteExpenseType(expenseType);
    }

    //Service Calls
    const sortStatusByOrder = (a: CaseStatus, b: CaseStatus) => {
        return (a.order || 0) - (b.order || 0);
    }
    const moveStatusToIndex = (caseStatus: CaseStatus, newIndex: number) => {
        const otherStats = caseDataState.caseData?.statuses.filter(x => x.systemStatus != caseStatus.systemStatus);
        let siblingStats = caseDataState.caseData?.statuses.filter(x => x.systemStatus == caseStatus.systemStatus).sort(sortStatusByOrder);

        const existingIndex = siblingStats?.findIndex(x => x.status == caseStatus.status);

        if (existingIndex !== undefined && existingIndex >= 0) {
            //remove it from the list
            siblingStats?.splice(existingIndex, 1);
            //insert at new index
            siblingStats?.splice(newIndex, 0, caseStatus);

            siblingStats = siblingStats?.map((cs, idx): CaseStatus => {
                return {
                    ...cs,
                    order: idx
                }
            });

            upsertCaseStatuses(siblingStats as CaseStatus[]);
        }

        // const newStats = [...(otherStats || []), ...(siblingStats || [])];
        // upsertCaseData({
        //     ...(caseDataState.caseData as CaseData),
        //     statuses: newStats
        // });
        // caseDataStateActions.setCaseStatuses(newStats);
    }
    const getStatuses = () => {
        const stats = [...caseDataState.caseData?.statuses || []];

        const newStats = caseDataState.caseData?.statuses?.filter(x => x.systemStatus == CaseSystemStatus.new);
        if (!newStats?.length) stats.push({ status: CaseSystemStatus.new, systemStatus: CaseSystemStatus.new });

        const activeStats = caseDataState.caseData?.statuses?.filter(x => x.systemStatus == CaseSystemStatus.active);
        if (!activeStats?.length) stats.push({ status: CaseSystemStatus.active, systemStatus: CaseSystemStatus.active });

        const completedStats = caseDataState.caseData?.statuses?.filter(x => x.systemStatus == CaseSystemStatus.completed);
        if (!completedStats?.length) stats.push({ status: CaseSystemStatus.completed, systemStatus: CaseSystemStatus.completed });

        const closedStats = caseDataState.caseData?.statuses?.filter(x => x.systemStatus == CaseSystemStatus.closed);
        if (!closedStats?.length) stats.push({ status: CaseSystemStatus.closed, systemStatus: CaseSystemStatus.closed });

        const removedStats = caseDataState.caseData?.statuses?.filter(x => x.systemStatus == CaseSystemStatus.removed);
        if (!removedStats?.length) stats.push({ status: CaseSystemStatus.removed, systemStatus: CaseSystemStatus.removed });

        return stats;
    }
    const sortStatuses = (a: CaseStatus, b: CaseStatus) => {
        const systemStatusDiff = (Object.values(CaseSystemStatus).indexOf(a.systemStatus) ) - (Object.values(CaseSystemStatus).indexOf(b.systemStatus) );
        return systemStatusDiff !== 0 ? systemStatusDiff : (a.order || 0) - (b.order || 0);
    }
    const getCaseStatus = (cs: CaseStatus): CaseStatus => {
        return getStatuses().find(x => x.systemStatus == cs?.systemStatus && x.status == cs?.status) || cs;
    }
    const getTheseStatuses = (statuses?: CaseStatus[]): CaseStatus[] => {
        if (!statuses?.length) return [] as CaseStatus[];
        return statuses.map((cs) => getCaseStatus(cs)) as CaseStatus[];
    }

    return {
        loadCaseDataAsync,
        caseData: caseDataState.caseData,
        caseTypes: caseDataState.caseData?.types || [],
        caseStatuses: getStatuses().sort(sortStatuses),
        getCaseStatus,
        getTheseStatuses: (statuses?: CaseStatus[]) => (getTheseStatuses(statuses)).sort(sortStatuses),
        userCreatedStatuses: caseDataState.caseData?.statuses || [],
        expenseTypes: caseDataState.caseData?.expenseTypes || [],
        addCaseType: addCaseType,
        removeCaseType: removeCaseType,
        addCaseStatus: addCaseStatus,
        upsertCaseStatuses: upsertCaseStatuses,
        removeCaseStatus: removeCaseStatus,
        moveStatusToIndex: moveStatusToIndex,
        addExpenseType: addExpenseType,
        removeExpenseType: removeExpenseType,
    }
}